import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers, Clock, ShieldCheck, MapPin } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import { useSEO, useJsonLd, SITE_URL, breadcrumbSchema } from "@/lib/seo";
import { GoAndTrackLink } from "@/lib/goAndTrack";

export default function DisposableSmartLabels() {
  useSEO({
    title: "Disposable Smart Labels | Single-Use Shipment Tracking",
    description: "Disposable smart labels for shipment visibility. Single-use, peel-and-stick tracking for high-value, temporary, and pilot logistics use cases.",
    canonical: "/disposable-smart-labels",
  });
  useJsonLd("service", {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Disposable Smart Labels",
    alternateName: [
      "Single-Use Smart Labels",
      "Bluetooth Smart Labels",
      "Disposable GPS Tracker",
      "Disposable GPS Tracking Stickers",
      "GPS Tracker for Packages",
      "Package Tracking Device",
      "GPS Package Tracking Devices",
      "Tracking Device for Packages",
      "Small Tracking Devices for Packages",
      "Shipment Tracking Devices",
    ],
    serviceType: "Shipment Visibility / Logistics Tracking",
    category: "Shipment Visibility / Logistics Tracking",
    description:
      "Single-use, peel-and-stick smart labels for shipment visibility — designed for high-value shipments, temporary tracking, package visibility, and logistics pilots.",
    provider: {
      "@type": "Organization",
      name: "Disposable Smart Labels",
      url: SITE_URL,
    },
    url: `${SITE_URL}/disposable-smart-labels`,
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Logistics, supply chain, operations, and innovation teams",
    },
    areaServed: [
      { "@type": "Place", name: "United Kingdom" },
      { "@type": "Place", name: "European Union" },
    ],
    additionalProperty: [
      { "@type": "PropertyValue", name: "Form factor", value: "Single-use peel-and-stick" },
      { "@type": "PropertyValue", name: "Connectivity", value: "Bluetooth Low Energy (BLE)" },
      { "@type": "PropertyValue", name: "Platform", value: "GoAndTrack" },
    ],
  });

  useJsonLd(
    "breadcrumb-dsl-page",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "Disposable Smart Labels" },
    ]),
  );

  return (
    <div data-testid="page-disposable">
      <PageHero
        eyebrow="Category overview"
        title="Disposable Smart Labels"
        subtitle="Single-use, peel-and-stick smart labels for shipment visibility — built for operations teams that need tracking without reverse logistics overhead."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/disposable-vs-reusable-trackers" className="btn-secondary" data-testid="page-cta-vs">
          Compare with reusable trackers
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Definition</p>
            <h2 className="h2">What is a disposable smart label?</h2>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <p className="body-text">
              A disposable smart label is a thin, single-use tracking device
              that is applied directly to a shipment, parcel, carton, or pallet
              and discarded at end-of-journey. Unlike reusable trackers, there
              is no requirement to recover, recharge, or redistribute hardware
              after the shipment closes.
            </p>
            <p className="body-text">
              Operations buyers commonly search for the same product under
              other names —{" "}
              <Link to="/disposable-gps-tracker" className="text-brand hover:underline">
                disposable GPS tracker
              </Link>
              , disposable GPS tracking sticker,{" "}
              <Link to="/gps-tracker-for-packages" className="text-brand hover:underline">
                GPS tracker for packages
              </Link>
              , package tracking device, tracking device for packages, or small
              tracking device for packages. Industry-side, the term used is
              disposable smart label. The product, the use case, and the
              operational model are the same.
            </p>
            <p className="body-text">
              Our disposable smart labels are designed for the
              operational realities of European logistics — application on the
              dispatch floor, transit through multi-leg networks, and clean
              shipment monitoring delivered into <GoAndTrackLink /> for ops, partners,
              and customers.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Characteristics</p>
            <h2 className="h2">What defines this category.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {[
              { icon: Layers, k: "Single-use form factor", v: "Designed for one shipment journey. No pairing, configuration, or recovery." },
              { icon: Clock, k: "Fast deployment", v: "Pilot lanes operational within days. No infrastructure standup required." },
              { icon: MapPin, k: "Stays with the asset", v: "Travels with the shipment, providing one clean dataset per journey." },
              { icon: ShieldCheck, k: "Operationally low-risk", v: "No expensive hardware lost in transit, depots, or returns." },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <b.icon className="h-5 w-5 text-brand" />
                <h3 className="h3 mt-4">{b.k}</h3>
                <p className="body-text mt-2 text-sm">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">When buyers reach for disposable</p>
            <h2 className="h2">Operational triggers.</h2>
            <ul className="mt-8 space-y-4">
              {[
                "You need shipment visibility for a defined period or programme, not perpetually.",
                "You can't reliably recover reusable trackers from end customers, partners, or returns.",
                "Your finance team prefers OpEx visibility costs over CapEx hardware ownership.",
                "You're piloting a new lane, partner, or product launch and need data without infrastructure commitment.",
                "You ship high-value or sensitive goods and want independent journey evidence.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white p-8 lg:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                Internal links
              </p>
              <h3 className="h3 mt-3">Explore by use case</h3>
              <div className="mt-6 divide-y divide-line">
                {[
                  { to: "/disposable-gps-tracker", label: "Disposable GPS Tracker — also-known-as" },
                  { to: "/gps-tracker-for-packages", label: "GPS Tracker for Packages — package-level" },
                  { to: "/smart-labels-for-logistics", label: "Smart Labels for Logistics" },
                  { to: "/smart-labels-for-high-value-shipments", label: "Smart Labels for High-Value Shipments" },
                  { to: "/smart-labels-for-pilot-projects", label: "Smart Labels for Pilot Projects" },
                  { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels" },
                  { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers" },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="flex items-center justify-between py-4 text-sm font-medium text-ink hover:text-brand transition-colors"
                    data-testid={`link-${l.to.replace(/\//g, "")}`}
                  >
                    {l.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip
        title="See if disposable smart labels fit your operation."
        body="A specialist will scope a structured pilot against a real shipment lane."
      />
    </div>
  );
}
